import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { PRINCIPLES, SITE } from "@/data/config";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import CTABlock from "@/components/CTABlock";
import { organizationSchema, websiteSchema, webPageSchema, breadcrumbSchema } from "@/lib/schemas";

// Editorial section wrappers
const Section = ({ children, testId, className = "" }) => (
  <section data-testid={testId} className={`bg-black border-b border-white/10 ${className}`}>
    <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-32">{children}</div>
  </section>
);

const OPERATING_STAGES = [
  {
    title: "First conversation",
    body:
      "A working call with studio leadership. No pitch, no discovery template. You describe what is not working. Kostaja names the honest starting point, even if the honest starting point is not a Kostaja engagement.",
  },
  {
    title: "Written intake and scope",
    body:
      "Once the fit is clear, Kostaja writes down the problem, the outcome you are buying, the scope, the timeline, and the price. Nothing important lives in a verbal quote.",
  },
  {
    title: "The work itself",
    body:
      "The person you met on the first call is the person doing the strategy, writing, and design decisions through delivery. No account manager layer. No junior handoff.",
  },
  {
    title: "Ship, review, adjust",
    body:
      "Work ships in visible increments so you never wait months to see a direction. Two review rounds are standard on most engagements. Feedback is treated as data, not as friction.",
  },
  {
    title: "Post-launch support",
    body:
      "Every project ends with a written handoff, a training walkthrough, and a support window. If ongoing work is a fit, it moves into a monthly scope. If not, it ends cleanly.",
  },
];

const STANDARDS = [
  {
    title: "Outcomes before methods",
    body:
      "Kostaja sells the effect the buyer gets. Not the deliverable list. Not the tool stack. The point of the work is what changes in the business, not what appears in the folder.",
  },
  {
    title: "Plain English, always",
    body:
      "No jargon. No agency vocabulary. If a sentence needs a translator, it does not survive the edit. Clarity is the fastest way to earn trust.",
  },
  {
    title: "Fixed where possible, scoped where not",
    body:
      "Smaller work is priced flat. Larger work is written down in a scope with milestone deposits. Pricing is tied to the work and the outcome, not the runway.",
  },
  {
    title: "One accountable partner",
    body:
      "You get a named studio lead. Not a rotating team, not a shared inbox, not a chain of forwarded emails.",
  },
  {
    title: "Discipline over decoration",
    body:
      "The visual system is a tool, not the point. Trend-driven work looks good in a portfolio and quiet in a market. Kostaja builds for the second one.",
  },
  {
    title: "No dead ends",
    body:
      "Every project ends with something the operator can maintain: a working site, a working system, a written brand guide, a monthly rhythm. Nothing ships that only Kostaja can operate.",
  },
  {
    title: "Honest portfolio presentation",
    body:
      "Kostaja does not publish invented metrics or fictional testimonials. Case studies describe what changed in the operator's day, not numbers manufactured for a slide deck.",
  },
  {
    title: "Useful before the first call",
    body:
      "The site is written to help buyers make a better decision even if they never contact Kostaja. Guides, comparisons, and glossary pages exist for that reason.",
  },
];

const About = () => {
  useSeo({
    ...SEO.about,
    jsonLd: [
      organizationSchema(),
      websiteSchema(),
      webPageSchema({ path: "/about", name: SEO.about.title, description: SEO.about.description }),
      breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
    ],
  });
  return (
    <main data-testid="about-page" className="bg-black text-white">
      {/* HERO */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16 pb-16 lg:pb-24">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "About" }]} />

          <div className="mt-10 lg:mt-16 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] font-black text-[#FFC600]">
            <span className="block w-8 h-px bg-[#FFC600]" aria-hidden />
            <span>About Kostaja</span>
          </div>

          <h1
            data-testid="about-headline"
            className="mt-6 font-black tracking-[-0.035em] leading-[0.9] text-white"
            style={{ fontSize: "clamp(52px, 7vw, 112px)" }}
          >
            A studio built to close the gap between{" "}
            <span className="k-underline-yellow">what a business is and what it looks like.</span>
          </h1>

          <p className="mt-8 lg:mt-10 text-lg lg:text-2xl leading-[1.35] font-medium text-white/85 max-w-3xl">
            Kostaja is a founder-led Canadian brand marketing studio based in {SITE.region}, working with service businesses across Canada. The work is brand, website, marketing, and the connective tissue between them.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <PrimaryButton to="/contact" testId="about-primary-cta">Start a Project</PrimaryButton>
            <SecondaryButton to="/portfolio" testId="about-secondary-cta">View Selected Work</SecondaryButton>
          </div>
        </div>
      </section>

      {/* WHY THE STUDIO EXISTS — big pull statement */}
      <Section testId="about-why">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4">
            <Overline color="yellow" className="mb-4">Why the studio exists</Overline>
            <div className="hidden lg:block text-[144px] leading-[0.85] font-black text-white/[0.06] select-none">01</div>
          </div>
          <div className="lg:col-span-8">
            <p className="text-2xl sm:text-3xl lg:text-[44px] font-semibold tracking-[-0.02em] leading-[1.18] text-white">
              Most service businesses look smaller than they are. The operation is real, the work is real, and the people are real. The outside just has not caught up.
            </p>
            <div className="mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl text-white/80 text-base lg:text-lg leading-relaxed">
              <p>
                Kostaja exists to fix that gap. Not with a template. Not with a logo. With a connected brand, website, and marketing system that makes the outside prove what the operator already knows about the inside.
              </p>
              <p>
                Every decision the studio makes runs through one question: does this make the business easier to understand, trust, and choose? If the answer is no, the decision changes.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* HOW THE WORK RUNS */}
      <Section testId="about-how">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Overline color="yellow" className="mb-4">How the work runs</Overline>
            <h2 className="text-4xl lg:text-6xl font-black tracking-[-0.02em] leading-[0.95]">
              What actually happens after you reach out.
            </h2>
            <p className="mt-6 text-white/70 max-w-sm leading-relaxed">
              No mystery process. No hidden discovery phase. This is the sequence for every engagement, whether it is a strategy call or a full brand and website system.
            </p>
          </div>
          <div className="lg:col-span-8">
            <ol className="border-l border-white/20">
              {OPERATING_STAGES.map((stage, idx) => (
                <li key={stage.title} className="pl-8 lg:pl-12 pb-12 last:pb-0 relative">
                  <span className="absolute -left-[9px] top-1 inline-block w-4 h-4 bg-[#FFC600] border border-white/15" />
                  <div className="text-[10px] uppercase tracking-[0.24em] font-black text-white/55">
                    Stage 0{idx + 1}
                  </div>
                  <h3 className="mt-1 text-2xl lg:text-3xl font-black tracking-[-0.02em] leading-tight text-white">
                    {stage.title}
                  </h3>
                  <p className="mt-3 text-white/80 text-base lg:text-lg leading-relaxed max-w-2xl">
                    {stage.body}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Section>

      {/* STANDARDS AND PILLARS */}
      <Section testId="about-standards">
        <div className="mb-12 lg:mb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Overline color="yellow" className="mb-4">Standards</Overline>
            <h2 className="text-4xl lg:text-6xl font-black tracking-[-0.02em] leading-[0.95]">
              Eight lines Kostaja will not cross,{" "}
              <span className="k-yellow">regardless of the client.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:pb-2">
            <p className="text-white/70 leading-relaxed">
              These are commitments to the buyer, not marketing copy. When a client asks for something that violates a standard, Kostaja says no and explains why.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/15">
          {STANDARDS.map((s, idx) => (
            <div key={s.title} className="bg-black p-6 lg:p-8">
              <div className="flex items-baseline gap-4">
                <span className="text-2xl lg:text-3xl font-black text-[#FFC600] leading-none">
                  0{idx + 1}
                </span>
                <h3 className="text-xl lg:text-2xl font-black tracking-[-0.01em] leading-tight">
                  {s.title}
                </h3>
              </div>
              <p className="mt-4 text-white/75 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        {/* Compact studio-principles reprise */}
        <div className="mt-16 pt-10 border-t border-white/15">
          <Overline className="mb-4">Studio principles, in one line each</Overline>
          <ul className="flex flex-wrap gap-2">
            {PRINCIPLES.map((p, idx) => (
              <li
                key={p}
                className="text-[11px] lg:text-xs uppercase tracking-[0.18em] font-semibold text-white/70 border border-white/15 px-3 py-1.5"
              >
                <span className="text-[#FFC600] mr-2">0{idx + 1}</span>
                {p}
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* WHO YOU WORK WITH + COMMERCIAL STANDARDS */}
      <Section testId="about-who-and-money">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
          <div>
            <Overline color="yellow" className="mb-4">Who you work with</Overline>
            <h3 className="text-3xl lg:text-5xl font-black tracking-[-0.02em] leading-[0.95]">
              The person you meet is the person doing the work.
            </h3>
            <p className="mt-6 text-white/80 text-base lg:text-lg leading-relaxed max-w-lg">
              Kostaja is founder-led. There is no handoff between the first call and the delivery team, because there is no delivery team behind the founder. The strategy, the writing, and the design decisions are owned by studio leadership through launch.
            </p>
            <p className="mt-4 text-white/70 leading-relaxed max-w-lg">
              When specialists are needed (paid media, video, PR) Kostaja coordinates them under a single accountable scope. The client still has one point of contact.
            </p>
          </div>
          <div>
            <Overline color="yellow" className="mb-4">How pricing works</Overline>
            <h3 className="text-3xl lg:text-5xl font-black tracking-[-0.02em] leading-[0.95]">
              Fixed where possible. Written down when not.
            </h3>
            <p className="mt-6 text-white/80 text-base lg:text-lg leading-relaxed max-w-lg">
              Smaller services use a fixed price. Larger engagements are proposal-based with a written scope, a written timeline, and milestone deposits. Deposits are never sold as standalone services. Nothing important lives in a verbal quote.
            </p>
            <p className="mt-4 text-white/70 leading-relaxed max-w-lg">
              If there is a simpler starting point than what you asked for, Kostaja will say so, even if it makes the invoice smaller.
            </p>
          </div>
        </div>
      </Section>

      {/* ENTITY FACTS — kept as-is for SEO/AI answer engines */}
      <Section testId="about-entity-facts">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Overline color="yellow" className="mb-4">Entity facts</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-[-0.02em] leading-[0.95]">
              The consistent record.
            </h2>
            <p className="mt-4 text-white/70 max-w-sm leading-relaxed">
              These facts stay identical across the site, Google Business Profile, listings, and structured data so search engines and AI answer engines can verify Kostaja quickly.
            </p>
          </div>
          <div className="lg:col-span-8">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/15">
              {[
                { k: "Business name", v: "Kostaja Ltd." },
                { k: "Website", v: SITE.url.replace("https://", "") },
                { k: "Country", v: "Canada" },
                { k: "Primary category", v: "Brand marketing agency" },
                { k: "Primary audience", v: "Canadian service businesses" },
                { k: "Service area", v: "Canada-wide, with emphasis on Ontario" },
                { k: "Email", v: SITE.email },
                { k: "Phone", v: SITE.phoneDisplay },
              ].map((f) => (
                <div key={f.k} className="bg-black p-5">
                  <dt className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow">{f.k}</dt>
                  <dd className="mt-1 text-white font-semibold">{f.v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow mb-2">Core services</div>
              <p className="text-white/85 leading-relaxed">
                Branding, websites, social media, Google Business Profile optimization, local SEO, AI receptionist systems, print design, sales materials, and marketing systems.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/why-kostaja" className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 border-2 border-white/25 hover:border-[#FFC600] hover:text-[#FFC600] transition-colors">Why Kostaja →</Link>
              <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 border-2 border-white/25 hover:border-[#FFC600] hover:text-[#FFC600] transition-colors">Explore Services →</Link>
              <Link to="/service-business-marketing-canada" className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 border-2 border-white/25 hover:border-[#FFC600] hover:text-[#FFC600] transition-colors">Marketing for service businesses →</Link>
            </div>
          </div>
        </div>
      </Section>

      <CTABlock variant="default" />
    </main>
  );
};

export default About;
